import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import SecurityIcon from "@mui/icons-material/Security";
import QRCode from "qrcode";
import axios from "axios";

// onEnabled: called after the user has finished setup (closed the recovery codes step).
function TwoFactorAuthSection({ user, t, onEnabled }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enable dialog state
  const [enableOpen, setEnableOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [secret, setSecret] = useState("");
  const [provisioningUri, setProvisioningUri] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [activating, setActivating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  // Disable dialog state
  const [disableOpen, setDisableOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [disableError, setDisableError] = useState("");

  // Regenerate dialog state
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenCodes, setRegenCodes] = useState([]);

  // Snackbar
  const [copied, setCopied] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/users/2fa/status/");
      setStatus(response.data);
    } catch (err) {
      console.error("Error fetching 2FA status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Enable flow
  const handleStartEnable = async () => {
    setEnableOpen(true);
    setActiveStep(0);
    setCode("");
    setError("");
    setRecoveryCodes([]);
    setActivating(true);

    try {
      const response = await axios.post("/api/users/2fa/totp/activate/");
      setSecret(response.data.secret);
      setProvisioningUri(response.data.provisioning_uri);

      // Generate QR code data URL
      const dataUrl = await QRCode.toDataURL(response.data.provisioning_uri, {
        width: 200,
        margin: 2,
      });
      setQrDataUrl(dataUrl);
    } catch (err) {
      setError(err.response?.data?.error || t("twoFactor.activationError"));
    } finally {
      setActivating(false);
    }
  };

  const handleConfirmCode = async () => {
    setConfirming(true);
    setError("");

    try {
      const response = await axios.post("/api/users/2fa/totp/confirm/", { code });
      setRecoveryCodes(response.data.recovery_codes);
      setActiveStep(2);
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || t("twoFactor.invalidCode"));
    } finally {
      setConfirming(false);
    }
  };

  const handleCloseEnable = () => {
    const finished = activeStep === 2;
    setEnableOpen(false);
    setSecret("");
    setProvisioningUri("");
    setQrDataUrl("");
    setCode("");
    setError("");
    setActiveStep(0);
    if (finished && onEnabled) {
      onEnabled();
    }
  };

  // Disable flow
  const handleDisable = async () => {
    setDisabling(true);
    setDisableError("");

    try {
      await axios.post("/api/users/2fa/totp/deactivate/", { password });
      setDisableOpen(false);
      setPassword("");
      await fetchStatus();
    } catch (err) {
      setDisableError(err.response?.data?.error || t("twoFactor.deactivationError"));
    } finally {
      setDisabling(false);
    }
  };

  // Regenerate flow
  const handleRegenerate = async () => {
    setRegenerating(true);

    try {
      const response = await axios.post("/api/users/2fa/recovery-codes/regenerate/");
      setRegenCodes(response.data.codes);
      await fetchStatus();
    } catch (err) {
      console.error("Error regenerating recovery codes:", err);
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyCodes = (codes) => {
    navigator.clipboard.writeText(codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadCodes = (codes) => {
    const text = `Verstaani - ${t("twoFactor.recoveryCodes")}\n${"=".repeat(30)}\n\n${codes.join("\n")}\n\n${t("twoFactor.recoveryCodesWarning")}`;
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "verstaani-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!status) return null;

  const steps = [
    t("twoFactor.stepScanQR"),
    t("twoFactor.stepVerify"),
    t("twoFactor.stepRecoveryCodes"),
  ];

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <SecurityIcon fontSize="small" />
        {t("twoFactor.title")}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Chip
          label={status.enabled ? t("twoFactor.enabled") : t("twoFactor.disabled")}
          color={status.enabled ? "success" : "default"}
          size="small"
        />
        {status.enabled && status.has_recovery_codes && (
          <Typography variant="body2" color="text.secondary">
            {t("twoFactor.recoveryCodesRemaining", { count: status.recovery_codes_remaining })}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {!status.enabled ? (
          <Button variant="contained" size="small" onClick={handleStartEnable}>
            {t("twoFactor.enable")}
          </Button>
        ) : (
          <>
            {status.is_staff || status.required ? (
              <Tooltip title={status.is_staff ? t("twoFactor.staffRequired") : t("twoFactor.orgRequired")}>
                <span>
                  <Button variant="outlined" size="small" color="error" disabled data-testid="two-factor-disable">
                    {t("twoFactor.disable")}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => { setDisableOpen(true); setPassword(""); setDisableError(""); }}
                data-testid="two-factor-disable"
              >
                {t("twoFactor.disable")}
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => { setRegenOpen(true); setRegenCodes([]); }}
            >
              {t("twoFactor.regenerateCodes")}
            </Button>
          </>
        )}
      </Box>

      {/* Enable 2FA Dialog */}
      <Dialog open={enableOpen} onClose={activeStep === 2 ? handleCloseEnable : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>{t("twoFactor.setupTitle")}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box>
              {activating ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("twoFactor.scanQR")}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t("twoFactor.removeOldEntry")}
                  </Alert>

                  {qrDataUrl && (
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <img src={qrDataUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
                    </Box>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("twoFactor.manualEntry")}
                  </Typography>
                  <Box sx={{
                    bgcolor: "grey.50",
                    border: 1,
                    borderColor: "grey.300",
                    borderRadius: 1,
                    p: 1.5,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                      {secret}
                    </Typography>
                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {copied && (
                    <Typography variant="caption" color="success.main" sx={{ mb: 1 }}>
                      {t("Copied!")}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("twoFactor.enterCode")}
              </Typography>

              <TextField
                fullWidth
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  style: { textAlign: "center", fontSize: 24, letterSpacing: 8 },
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && code.length === 6) {
                    handleConfirmCode();
                  }
                }}
              />
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("twoFactor.recoveryCodesWarning")}
              </Alert>

              <Box sx={{
                bgcolor: "grey.50",
                border: 1,
                borderColor: "grey.300",
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  {recoveryCodes.map((code, i) => (
                    <Typography key={i} variant="body2" sx={{ fontFamily: "monospace" }}>
                      {code}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyCodes(recoveryCodes)}
                >
                  {copied ? t("Copied!") : t("twoFactor.copy")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadCodes(recoveryCodes)}
                >
                  {t("twoFactor.download")}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {activeStep === 0 && (
            <>
              <Button onClick={handleCloseEnable}>{t("Cancel")}</Button>
              <Button variant="contained" onClick={() => setActiveStep(1)} disabled={activating || !qrDataUrl}>
                {t("Next")}
              </Button>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Button onClick={() => { setActiveStep(0); setError(""); }}>{t("Back")}</Button>
              <Button
                variant="contained"
                onClick={handleConfirmCode}
                disabled={confirming || code.length !== 6}
              >
                {confirming ? <CircularProgress size={20} /> : t("twoFactor.verify")}
              </Button>
            </>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={handleCloseEnable}>
              {t("Done")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableOpen} onClose={() => setDisableOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("twoFactor.confirmDisable")}</DialogTitle>
        <DialogContent>
          {disableError && <Alert severity="error" sx={{ mb: 2 }}>{disableError}</Alert>}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("twoFactor.enterPassword")}
          </Typography>

          <TextField
            fullWidth
            autoFocus
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label={t("Password")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && password) {
                handleDisable();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisable}
            disabled={disabling || !password}
          >
            {disabling ? <CircularProgress size={20} /> : t("twoFactor.disable")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Recovery Codes Dialog */}
      <Dialog open={regenOpen} onClose={() => setRegenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("twoFactor.regenerateCodes")}</DialogTitle>
        <DialogContent>
          {regenCodes.length === 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("twoFactor.regenerateWarning")}
              </Alert>
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t("twoFactor.recoveryCodesWarning")}
              </Alert>

              <Box sx={{
                bgcolor: "grey.50",
                border: 1,
                borderColor: "grey.300",
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  {regenCodes.map((code, i) => (
                    <Typography key={i} variant="body2" sx={{ fontFamily: "monospace" }}>
                      {code}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyCodes(regenCodes)}
                >
                  {copied ? t("Copied!") : t("twoFactor.copy")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadCodes(regenCodes)}
                >
                  {t("twoFactor.download")}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {regenCodes.length === 0 ? (
            <>
              <Button onClick={() => setRegenOpen(false)}>{t("Cancel")}</Button>
              <Button
                variant="contained"
                onClick={handleRegenerate}
                disabled={regenerating}
              >
                {regenerating ? <CircularProgress size={20} /> : t("twoFactor.regenerate")}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setRegenOpen(false)}>
              {t("Done")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TwoFactorAuthSection;
